<script lang="ts" setup>
import { ref, reactive, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import TurnstileWidget from '@/components/register/TurnstileWidget.vue'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage, isRateLimited } from '@/composables/useAxiosError'
import { appConfig } from 'config/app'
import type { AbuseReportRequest, AbuseReportResponse } from '@/types'
import { isValidIpAddress } from '@/utils/ipAddress'
import { STATUS_CODES } from '@/constants'

defineOptions({ name: 'AbuseReportModal' })

const { t } = useI18n()

const props = defineProps<{
  modelValue: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// Form state
const form = reactive({
  reporter_email: '',
  reporter_name: '',
  target: 'domain' as 'domain' | 'ip',
  domain_name: '',
  reported_ip: '',
  url: '',
  category: '' as AbuseReportRequest['category'] | '',
  description: '',
  good_faith: false
})

// UI state
const loading = ref(false)
const success = ref(false)
const caseId = ref<number | null>(null)
// True when the reported domain is not one we operate. The notice is still
// recorded — we simply are not the right addressee, and say so.
const outOfScope = ref(false)
const outOfScopeKey = computed(() =>
  form.target === 'ip' ? 'abuse.success.outOfScopeIp' : 'abuse.success.outOfScope'
)
const captchaToken = ref<string | null>(null)
const turnstileKey = ref(0)
const errorMessage = ref<string | null>(null)

// Category options
const categoryOptions = computed(() => [
  { value: 'spam', title: t('abuse.categories.spam') },
  { value: 'phishing', title: t('abuse.categories.phishing') },
  { value: 'malware', title: t('abuse.categories.malware') },
  { value: 'fake_data', title: t('abuse.categories.fakeData') },
  { value: 'trademark', title: t('abuse.categories.trademark') },
  { value: 'other', title: t('abuse.categories.other') }
])

// Form validation
/**
 * Art. 16(2)(c) DSA requires the notifier's name, except for notices about
 * offences under Art. 3-7 of Directive 2011/93/EU, which must stay anonymous.
 * `other` is the catch-all where such a report would land, so it is exempt —
 * the backend applies the same rule.
 */
const nameRequired = computed(() => form.category !== '' && form.category !== 'other')

const ipValid = computed(() => isValidIpAddress(form.reported_ip.trim()))
const ipError = computed(() =>
  form.target === 'ip' && form.reported_ip.trim() !== '' && !ipValid.value ? t('abuse.form.ipInvalid') : ''
)

// A domain report points at the content; an IP report (spam from a server) may have no page to name.
const urlRequired = computed(() => form.target === 'domain')
const urlValid = computed(() => {
  const url = form.url.trim()
  return url === '' ? !urlRequired.value : /^https?:\/\//i.test(url)
})

const isFormValid = computed(() => {
  return (
    form.reporter_email.trim() !== '' &&
    (!nameRequired.value || form.reporter_name.trim() !== '') &&
    (form.target === 'domain' ? form.domain_name.trim() !== '' : ipValid.value) &&
    urlValid.value &&
    form.category !== '' &&
    form.description.trim().length >= 10 &&
    form.good_faith &&
    captchaToken.value !== null
  )
})

// Reset form when modal opens/closes
watch(() => props.modelValue, (isOpen) => {
  if (isOpen) {
    resetForm()
  }
})

function resetForm() {
  form.reporter_email = ''
  form.reporter_name = ''
  form.target = 'domain'
  form.domain_name = ''
  form.reported_ip = ''
  form.url = ''
  form.category = ''
  form.description = ''
  form.good_faith = false
  success.value = false
  caseId.value = null
  outOfScope.value = false
  captchaToken.value = null
  errorMessage.value = null
  turnstileKey.value++
}

function onCaptchaVerify(token: string) {
  captchaToken.value = token
}

function onCaptchaExpire() {
  captchaToken.value = null
}

function onCaptchaError() {
  captchaToken.value = null
  errorMessage.value = t('abuse.captchaError')
}

async function submitReport() {
  if (!isFormValid.value || loading.value) return

  loading.value = true
  errorMessage.value = null

  try {
    // The backend takes exactly one target, so the other one is left out.
    const payload = {
      reporter_email: form.reporter_email.trim(),
      reporter_name: form.reporter_name.trim() || undefined,
      ...(form.target === 'domain'
        ? { domain_name: form.domain_name.trim() }
        : { reported_ip: form.reported_ip.trim() }),
      url: form.url.trim() || undefined,
      category: form.category as AbuseReportRequest['category'],
      description: form.description.trim(),
      good_faith: form.good_faith,
      token: captchaToken.value
    }

    const res = await useAxios<typeof payload, AbuseReportResponse>(
      'POST',
      '/abuse/report',
      false,
      payload
    )

    if (res && (res.status === STATUS_CODES.OK || res.status === STATUS_CODES.CREATED)) {
      success.value = true
      caseId.value = res.data?.case_id || null
      outOfScope.value = res.data?.out_of_scope === true
    } else {
      throw new Error('Failed to submit report')
    }
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'AbuseReportModal' })
    if (isRateLimited(error)) {
      errorMessage.value = t('abuse.tooManyReports')
    } else {
      errorMessage.value = getErrorMessage(error as Error)
    }
    turnstileKey.value++
    captchaToken.value = null
  } finally {
    loading.value = false
  }
}

function closeModal() {
  isOpen.value = false
}
</script>

<template>
  <v-dialog
    v-model="isOpen"
    max-width="560"
    :persistent="loading"
    scrim="rgba(0, 0, 0, 0.7)"
  >
    <div class="abuse-report-modal">
      <!-- Close Button -->
      <v-btn
        icon
        variant="flat"
        size="small"
        class="close-btn"
        :disabled="loading"
        @click="closeModal"
      >
        <v-icon icon="mdi-close" />
      </v-btn>

      <!-- Success State -->
      <template v-if="success">
        <div class="success-state">
          <div class="success-icon">
            <v-icon icon="mdi-check-circle" size="64" />
          </div>
          <h3 class="success-title">{{ t('abuse.success.title') }}</h3>
          <p class="success-message">{{ outOfScope ? t(outOfScopeKey) : t('abuse.success.message') }}</p>
          <p v-if="caseId" class="case-id">
            {{ t('abuse.success.caseId', { id: caseId }) }}
          </p>
          <v-btn
            color="primary"
            class="mt-6"
            @click="closeModal"
          >
            {{ t('abuse.success.close') }}
          </v-btn>
        </div>
      </template>

      <!-- Form State -->
      <template v-else>
        <div class="modal-header">
          <v-icon icon="mdi-alert-circle" size="28" class="header-icon" />
          <div>
            <h2 class="modal-title">{{ t('abuse.title') }}</h2>
            <p class="modal-subtitle">{{ t('abuse.subtitle') }}</p>
          </div>
        </div>

        <v-form class="report-form" @submit.prevent="submitReport">
          <!-- Error Message -->
          <v-alert
            v-if="errorMessage"
            type="error"
            variant="tonal"
            closable
            class="mb-4"
            @click:close="errorMessage = null"
          >
            {{ errorMessage }}
          </v-alert>

          <!-- Email -->
          <div class="input-group">
            <label class="input-label">{{ t('abuse.form.email') }}</label>
            <v-text-field
              v-model="form.reporter_email"
              type="email"
              variant="outlined"
              density="comfortable"
              placeholder="name@example.com"
              :disabled="loading"
              class="glass-input"
            />
          </div>

          <!-- Name (optional) -->
          <div class="input-group">
            <label class="input-label">{{ nameRequired ? t('abuse.form.name') : t('abuse.form.nameOptional') }}</label>
            <v-text-field
              v-model="form.reporter_name"
              variant="outlined"
              density="comfortable"
              placeholder="Max Mustermann"
              :disabled="loading"
              class="glass-input"
            />
          </div>

          <!-- Target: a domain we operate, or an address of one of our servers -->
          <div class="input-group">
            <label class="input-label">{{ t('abuse.form.target') }}</label>
            <v-radio-group v-model="form.target" inline hide-details :disabled="loading" class="target-group">
              <v-radio value="domain" :label="t('abuse.form.targetDomain')" />
              <v-radio value="ip" :label="t('abuse.form.targetIp')" />
            </v-radio-group>
          </div>

          <div v-if="form.target === 'domain'" class="input-group">
            <label class="input-label">{{ t('abuse.form.domain') }}</label>
            <v-text-field
              v-model="form.domain_name"
              variant="outlined"
              density="comfortable"
              placeholder="example.de"
              :disabled="loading"
              class="glass-input"
            />
          </div>

          <div v-else class="input-group">
            <label class="input-label">{{ t('abuse.form.ip') }}</label>
            <v-text-field
              v-model="form.reported_ip"
              variant="outlined"
              density="comfortable"
              placeholder="203.0.113.10"
              :error-messages="ipError ? [ipError] : []"
              :disabled="loading"
              class="glass-input"
            />
          </div>

          <!-- Precise location (Art. 16(2)(b) DSA) -->
          <div class="input-group">
            <label class="input-label">{{ urlRequired ? t('abuse.form.url') : t('abuse.form.urlOptional') }}</label>
            <v-text-field
              v-model="form.url"
              variant="outlined"
              density="comfortable"
              placeholder="https://example.de/pfad/zur/seite"
              :hint="t('abuse.form.urlHint')"
              persistent-hint
              :disabled="loading"
              class="glass-input"
            />
          </div>

          <!-- Category -->
          <div class="input-group">
            <label class="input-label">{{ t('abuse.form.category') }}</label>
            <v-select
              v-model="form.category"
              :items="categoryOptions"
              item-title="title"
              item-value="value"
              variant="outlined"
              density="comfortable"
              :disabled="loading"
              class="glass-input"
            />
          </div>

          <!-- Description -->
          <div class="input-group">
            <label class="input-label">{{ t('abuse.form.description') }}</label>
            <v-textarea
              v-model="form.description"
              variant="outlined"
              density="comfortable"
              rows="4"
              :disabled="loading"
              :counter="2000"
              :rules="[(v) => v.length >= 10 || t('abuse.form.descriptionMinLength')]"
              class="glass-input"
            />
          </div>

          <!-- Good-faith declaration (Art. 16(2)(d) DSA) -->
          <div class="input-group">
            <v-checkbox
              v-model="form.good_faith"
              :label="t('abuse.form.goodFaith')"
              density="comfortable"
              hide-details
              :disabled="loading"
            />
          </div>

          <!-- Captcha -->
          <div class="captcha-wrapper">
            <TurnstileWidget
              :key="turnstileKey"
              :site-key="appConfig.env.turnstileSiteKey"
              theme="dark"
              render-on-mount
              @token="onCaptchaVerify"
              @expired="onCaptchaExpire"
              @error="onCaptchaError"
            />
          </div>

          <!-- Submit Button -->
          <v-btn
            type="submit"
            color="primary"
            block
            size="large"
            :loading="loading"
            :disabled="!isFormValid"
            class="submit-btn"
          >
            <v-icon icon="mdi-send" class="me-2" />
            {{ t('abuse.form.submit') }}
          </v-btn>

          <!-- Privacy Notice -->
          <p class="privacy-notice">
            {{ t('abuse.privacyNotice') }}
          </p>
        </v-form>
      </template>
    </div>
  </v-dialog>
</template>

<style lang="scss" scoped>
.abuse-report-modal {
  position: relative;
  padding: 32px;
  background: rgba(15, 23, 48, 0.95);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 20px;
}

.close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.08) !important;
  color: rgba(255, 255, 255, 0.6) !important;

  &:hover {
    background: rgba(255, 255, 255, 0.15) !important;
    color: rgba(255, 255, 255, 0.9) !important;
  }
}

/* Modal Header */
.modal-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 28px;
}

.header-icon {
  color: #ff9800;
  flex-shrink: 0;
}

.modal-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 6px;
}

.modal-subtitle {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.6);
  line-height: 1.4;
  margin: 0;
}

/* Form */
.report-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.input-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.8);
}

.glass-input {
  /* Styles moved to unscoped block for Vuetify component overrides */
}

.captcha-wrapper {
  display: flex;
  justify-content: center;
  margin: 8px 0;
}

.submit-btn {
  height: 52px !important;
  font-size: 0.95rem !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 12px !important;
  margin-top: 8px;
}

.privacy-notice {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.45);
  text-align: center;
  line-height: 1.5;
  margin: 8px 0 0 0;
}

/* Success State */
.success-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px 0;
}

.success-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 96px;
  background: rgba(76, 175, 80, 0.1);
  border-radius: 50%;
  margin-bottom: 20px;
  color: #4caf50;
}

.success-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 12px;
}

.success-message {
  font-size: 0.95rem;
  color: rgba(200, 218, 255, 0.7);
  line-height: 1.5;
  margin: 0;
}

.case-id {
  font-size: 0.9rem;
  color: rgba(0, 176, 255, 0.9);
  font-weight: 500;
  margin: 16px 0 0 0;
  padding: 10px 16px;
  background: rgba(0, 176, 255, 0.1);
  border-radius: 8px;
}

/* Light Theme */
.v-theme--lightTheme {
  .abuse-report-modal {
    background: rgba(255, 255, 255, 0.98);
    border-color: rgba(28, 110, 227, 0.12);
  }

  .close-btn {
    background: rgba(0, 0, 0, 0.05) !important;
    color: rgba(0, 0, 0, 0.5) !important;

    &:hover {
      background: rgba(0, 0, 0, 0.1) !important;
      color: rgba(0, 0, 0, 0.8) !important;
    }
  }

  .modal-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .modal-subtitle {
    color: rgba(15, 23, 42, 0.6);
  }

  .input-label {
    color: rgba(15, 23, 42, 0.8);
  }


  .privacy-notice {
    color: rgba(15, 23, 42, 0.45);
  }

  .success-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .success-message {
    color: rgba(15, 23, 42, 0.7);
  }

  .case-id {
    background: rgba(28, 110, 227, 0.08);
    color: #1c6ee3;
  }
}

/* Mobile */
@media (max-width: 600px) {
  .abuse-report-modal {
    padding: 24px 20px;
    border-radius: 16px;
  }

  .modal-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .modal-title {
    font-size: 1.2rem;
  }
}
</style>

<style lang="scss">
/* Unscoped styles for Vuetify deep component overrides */
.glass-input .v-field {
  background: rgba(255, 255, 255, 0.04);
  border-radius: 10px;
}

.glass-input .v-field__outline__start,
.glass-input .v-field__outline__end,
.glass-input .v-field__outline__notch::before,
.glass-input .v-field__outline__notch::after {
  border-color: rgba(120, 163, 255, 0.2);
}

.glass-input .v-field--focused .v-field__outline__start,
.glass-input .v-field--focused .v-field__outline__end,
.glass-input .v-field--focused .v-field__outline__notch::before,
.glass-input .v-field--focused .v-field__outline__notch::after {
  border-color: rgba(0, 176, 255, 0.5);
}

.glass-input input,
.glass-input textarea {
  color: rgba(255, 255, 255, 0.9);
}

.glass-input input::placeholder,
.glass-input textarea::placeholder {
  color: rgba(200, 218, 255, 0.4);
}

/* Light Theme */
.v-theme--lightTheme .glass-input .v-field {
  background: rgba(0, 0, 0, 0.02);
}

.v-theme--lightTheme .glass-input .v-field__outline__start,
.v-theme--lightTheme .glass-input .v-field__outline__end,
.v-theme--lightTheme .glass-input .v-field__outline__notch::before,
.v-theme--lightTheme .glass-input .v-field__outline__notch::after {
  border-color: rgba(28, 110, 227, 0.15);
}

.v-theme--lightTheme .glass-input input,
.v-theme--lightTheme .glass-input textarea {
  color: rgba(15, 23, 42, 0.9);
}

.v-theme--lightTheme .glass-input input::placeholder,
.v-theme--lightTheme .glass-input textarea::placeholder {
  color: rgba(15, 23, 42, 0.4);
}
</style>
